import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { Supplier, PermissionNames } from '@getgreenline/homi-shared'
import AppHeader from '../../../components/AppView/Header'
import { AppView, AppMainView } from '../../../components/AppView/index'
import { CreateSupplierTab } from './CreateSupplierTab'
import { Skeleton, Table, Tooltip, Dropdown, Menu, Button } from 'antd'
import { NoPermissionsResult } from '../../../components/NoPermissionsResult'
import { HelpLinkMenu } from '../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'
import { DeleteSupplierModal } from './DeleteSupplierModal'
import { Link } from 'react-router'
import { message } from 'antd-v4'
import { HelpLinks } from '../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface State {
  showEditModal: boolean
  showDeleteModal: boolean
  selectedSupplier: Supplier | null
}

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: 300,
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    className: 'table-col-truncate',
    width: 200,
    render: (description: string) => (
      <Tooltip overlayStyle={{ whiteSpace: 'pre-line' }} placement='topLeft' title={description}>
        {description}
      </Tooltip>
    ),
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    width: 200,
  },
  {
    title: 'Phone',
    dataIndex: 'phone',
    key: 'phone',
    width: 150,
  },
  {
    title: 'Address',
    dataIndex: 'address',
    key: 'address',
    className: 'table-col-truncate',
    render: (address: string) => (
      <Tooltip overlayStyle={{ whiteSpace: 'pre-line' }} placement='topLeft' title={address}>
        {address}
      </Tooltip>
    ),
    width: 150,
  },
  {
    title: '',
    dataIndex: 'options',
    key: 'options',
    width: 75,
  },
]

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class DashboardSuppliers extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      showDeleteModal: false,
      showEditModal: false,
      selectedSupplier: null,
    }
  }

  showPermissionErrorMessage = (permission: PermissionNames) => {
    message.error(
      `Your account does not have the "${permission}" permission. Please ask your administrator for access.`,
    )
  }
  componentDidMount() {
    this.props.currentCompanyStore!.supplierStore!.getSuppliers()
  }
  handleSupplierOnClick() {
    const canEditVendors = this.props.currentUserStore?.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_VENDORS,
      this.props.currentCompanyStore!.company!.id,
    )
    if (!canEditVendors) {
      this.showPermissionErrorMessage(PermissionNames.CAN_EDIT_VENDORS)
      return
    }
  }
  get tableDataSource() {
    const suppliers = this.props.currentCompanyStore!.supplierStore!.suppliers
    const canEditVendors = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_VENDORS,
      this.company.id,
    )

    if (!suppliers) {
      return []
    }

    return suppliers.map((supplier) => {
      return {
        name: (
          <Link
            style={{
              cursor: canEditVendors ? 'pointer' : 'not-allowed',
            }}
            to={''}
            onClick={() => {
              this.handleSupplierOnClick()
              this.setState({
                showEditModal: canEditVendors,
                selectedSupplier: supplier,
              })
            }}
          >
            {supplier.name}
          </Link>
        ),
        description: supplier.description,
        email: supplier.email,
        phone: supplier.phone,
        address: supplier.address,
        options: canEditVendors && (
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.Item
                  key='edit'
                  onClick={() =>
                    this.setState({
                      showEditModal: true,
                      selectedSupplier: supplier,
                    })
                  }
                >
                  <span style={{ color: 'var(--blue)' }}>Edit</span>
                </Menu.Item>

                <Menu.Item
                  key='delete'
                  onClick={() =>
                    this.setState({
                      showDeleteModal: true,
                      selectedSupplier: supplier,
                    })
                  }
                >
                  <span style={{ color: 'var(--red)' }}>Delete</span>
                </Menu.Item>
              </Menu>
            }
          >
            <Button icon='ellipsis' style={{ transform: 'rotate(90deg)' }} />
          </Dropdown>
        ),
      }
    })
  }

  render() {
    if (
      !this.props.currentUserStore!.hasPermissionAtEntity(
        PermissionNames.CAN_VIEW_VENDORS,
        this.company.id,
      )
    ) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_VENDORS} />
    }

    const { selectedSupplier, showDeleteModal, showEditModal } = this.state
    const suppliers = this.props.currentCompanyStore!.supplierStore!.suppliers
    const canEditVendors = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_VENDORS,
      this.company.id,
    )

    let body: JSX.Element
    if (!suppliers) {
      body = <Skeleton active />
    } else {
      body = (
        <Table
          dataSource={this.tableDataSource}
          columns={columns}
          className='no-background-table intercom-pb-3'
          rowClassName={() => 'background-white'}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      )
    }

    return (
      <AppView column>
        <Helmet>
          <title>Products | Vendors</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <AppHeader title='Brands & Vendors'>
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={{
                href: HelpLinks.PRODUCTS_BRANDS_VENDORS,
                title: 'Adding vendors',
              }}
            />

            <BlazeButton
              buttonProps={{
                type: 'primary',
                icon: 'plus',
                disabled: !canEditVendors,
                onClick: () =>
                  this.setState({
                    showEditModal: true,
                    selectedSupplier: null,
                  }),
              }}
              titleProps={{
                title: `Add brand/vendor`,
              }}
            />
          </span>
        </AppHeader>
        <AppMainView className='background-grey'>{body}</AppMainView>

        {showEditModal && (
          <CreateSupplierTab
            supplier={selectedSupplier}
            onHide={() => this.setState({ showEditModal: false })}
          />
        )}

        {showDeleteModal && selectedSupplier && (
          <DeleteSupplierModal
            supplier={selectedSupplier}
            onClose={() => this.setState({ showDeleteModal: false })}
          />
        )}
      </AppView>
    )
  }
}
